import React from 'react';
import { Info } from 'lucide-react';
import { ViewHint } from './ViewHint';
import type { Product } from '../../pages/CatalogPage';
import { sortByAvailability } from '../../utils/tableHelpers';
import type { Country } from '../../types';

type PartnerIntegrationsTableProps = {
  products: Product[];
  visibleCountries: Country[];
  browseMode: boolean;
  selectedCountry: string | null;
  highlightedProduct: string | null;
  onPartnerClick: (product: Product) => void;
}

export function PartnerIntegrationsTable({
  products,
  visibleCountries,
  browseMode,
  selectedCountry,
  highlightedProduct,
  onPartnerClick,
}: PartnerIntegrationsTableProps) {
  const columnWidth = visibleCountries.length >= 14 ? '45px' : '60px';
  const sortedProducts = sortByAvailability(products);

  return (
    <div className="bg-card border border-border rounded-lg overflow-x-auto">
      <table className="w-full min-w-[600px]" style={{ tableLayout: 'fixed' }}>
        <thead className="bg-muted/50 sticky top-0 z-10 lg:hidden">
          <tr className="h-16">
            <th
              className="text-left px-4 py-2 text-sm font-medium text-foreground"
              style={{ width: '180px' }}
            >
              Partner
            </th>
            <th
              className="text-left px-4 py-2 text-sm font-medium text-foreground"
              style={{ width: '180px' }}
            >
              Solution
            </th>
            <th
              className="text-left px-4 py-2 text-sm font-medium text-foreground"
              style={{ width: '120px' }}
            >
              Hardware Family
            </th>
            <th
              className="text-left px-4 py-2 text-sm font-medium text-foreground"
              style={{ width: '100px' }}
            >
              Integration Type
            </th>
            <th
              className="text-left px-4 py-2 text-sm font-medium text-foreground"
              style={{ width: '80px' }}
            >
              Version
            </th>
            <th
              className="text-center px-4 py-2 text-sm font-medium text-foreground"
              style={{ width: '100px' }}
            >
              <div className="flex flex-col items-center gap-0.5">
                <span>Shift4 Mobile</span>
                <span className="text-xs text-muted-foreground font-normal">
                  (Pay / Order at the Table)
                </span>
              </div>
            </th>
            {browseMode
              ? visibleCountries.map(country => (
                  <th
                    key={country.code}
                    className="text-center px-1 py-2 font-medium text-foreground relative group cursor-default select-none"
                    style={{ width: columnWidth }}
                    title={country.name}
                  >
                    <div className="flex flex-col items-center justify-center h-full">
                      {visibleCountries.length >= 14 ? (
                        <span className="text-xs select-none whitespace-nowrap transform -rotate-45 origin-center">
                          {country.code}
                        </span>
                      ) : (
                        <div className="flex flex-col items-center justify-center h-full gap-1">
                          <span className="text-sm select-none">{country.flag}</span>
                          <span className="text-xs select-none whitespace-nowrap">
                            {country.code}
                          </span>
                        </div>
                      )}
                    </div>
                  </th>
                ))
              : selectedCountry && (
                  <th
                    className="text-center px-1 py-2 font-medium text-foreground relative group cursor-default select-none"
                    style={{ width: columnWidth }}
                  >
                    <div className="flex flex-col items-center justify-center h-full">
                      {visibleCountries.length >= 14 ? (
                        <span className="text-xs select-none whitespace-nowrap transform -rotate-45 origin-center">
                          {selectedCountry}
                        </span>
                      ) : (
                        <div className="flex flex-col items-center justify-center h-full gap-1">
                          <span className="text-xs select-none whitespace-nowrap">
                            {selectedCountry}
                          </span>
                        </div>
                      )}
                    </div>
                  </th>
                )}
          </tr>
        </thead>
        <tbody>
          {sortedProducts.map((product, index) => {
            const isHighlighted = highlightedProduct && product.solution === highlightedProduct;
            return (
              <tr
                key={index}
                className={`border-b border-border/50 hover:bg-blue-50 dark:hover:bg-blue-900/20 hover:shadow-sm cursor-pointer group transition-colors ${
                  isHighlighted ? 'bg-yellow-100 dark:bg-yellow-900/30 animate-pulse' : ''
                }`}
                onClick={() => onPartnerClick(product)}
                data-product-name={product.solution}
              >
                <td className="px-4 py-3 text-sm font-medium truncate" style={{ width: '180px' }}>
                  <div className="flex items-center justify-between">
                    <div className="flex items-center gap-2">
                      <div className="font-medium text-foreground text-sm leading-tight truncate">
                        {product.solution}
                      </div>
                      {product.notes && (
                        <div title="Additional details available">
                          <Info className="w-3 h-3 text-blue-500 flex-shrink-0" />
                        </div>
                      )}
                    </div>
                    <ViewHint />
                  </div>
                </td>
                <td
                  className="px-4 py-3 text-sm text-muted-foreground truncate"
                  style={{ width: '180px' }}
                >
                  {product.solution_description || '-'}
                </td>
                <td
                  className="px-4 py-3 text-sm text-muted-foreground truncate"
                  style={{ width: '120px' }}
                >
                  {product.hardware_family || '-'}
                </td>
                <td
                  className="px-4 py-3 text-sm text-muted-foreground truncate"
                  style={{ width: '100px' }}
                >
                  {product.model || '-'}
                </td>
                <td
                  className="px-4 py-3 text-xs text-muted-foreground truncate"
                  style={{ width: '80px' }}
                >
                  {product.tag || '-'}
                </td>
                <td className="px-4 py-3 text-sm text-center" style={{ width: '100px' }}>
                  {product.patt && product.patt.trim() !== '' ? (
                    <span className="text-muted-foreground">{product.patt}</span>
                  ) : (
                    <span className="text-transparent">-</span>
                  )}
                </td>
                {browseMode ? (
                  visibleCountries.map(country => {
                    const value = product.countries?.[country.code];
                    return (
                      <td
                        key={country.code}
                        className="text-center px-1 py-2"
                        style={{ width: columnWidth }}
                      >
                        {value === true ? (
                          <span className="text-sm text-green-600">✓</span>
                        ) : value === 'Coming Soon' ? (
                          <span className="text-[10px] italic text-muted-foreground">Soon</span>
                        ) : value === 'N/A' ? (
                          <span className="text-muted-foreground text-xs">—</span>
                        ) : (
                          <span className="text-sm text-red-500">✗</span>
                        )}
                      </td>
                    );
                  })
                ) : (
                  selectedCountry &&
                    (() => {
                      const value = product.countries?.[selectedCountry];
                      return (
                        <td className="text-center px-1 py-2" style={{ width: columnWidth }}>
                          {value === true ? (
                            <span className="text-sm text-green-600">✓</span>
                          ) : value === 'Coming Soon' ? (
                            <span className="text-[10px] italic text-muted-foreground">Soon</span>
                          ) : value === 'N/A' ? (
                            <span className="text-muted-foreground text-xs">—</span>
                          ) : (
                            <span className="text-sm text-red-500">✗</span>
                          )}
                        </td>
                      );
                    })()
                )}
              </tr>
            );
          })}
        </tbody>
      </table>
    </div>
  );
};
